import React, { useState, useRef, useEffect } from 'react';
import { Camera, QrCode, Smartphone, RefreshCw, Check, ArrowRight, ShieldCheck } from 'lucide-react';

const STEPS = [
  { id: 'front', label: '1. Mirando al Frente', hint: 'Mira directamente al lente y mantén una expresión natural' },
  { id: 'left', label: '2. Perfil Izquierdo (45°)', hint: 'Gira lentamente la cabeza hacia la izquierda' },
  { id: 'right', label: '3. Perfil Derecho (45°)', hint: 'Gira lentamente la cabeza hacia la derecha' },
  { id: 'up', label: '4. Ángulo Elevado', hint: 'Inclina suavemente la barbilla hacia arriba' }
];

export function FaceScannerModal({
  isOpen,
  onClose,
  onSaveAngles,
  avatarName = 'Mi Avatar'
}) {
  const [mode, setMode] = useState('webcam'); // 'webcam' | 'qr'
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [capturedFrames, setCapturedFrames] = useState([]);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [countdown, setCountdown] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCameraActive(false);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720, facingMode: 'user' }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setIsCameraActive(true);
    } catch (err) {
      console.error('Error accediendo a la cámara:', err);
      setIsCameraActive(false);
    }
  };

  useEffect(() => {
    if (isOpen && mode === 'webcam') {
      startCamera();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen, mode]);

  const [sessionId] = useState(() => `scan_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`);
  const [isReceivingMobile, setIsReceivingMobile] = useState(false);

  // Polling automático cuando el usuario está en la pestaña QR
  useEffect(() => {
    if (!isOpen || mode !== 'qr') return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/scan/session/poll?sessionId=${encodeURIComponent(sessionId)}`);
        const data = await res.json();
        if (data.success && data.session && data.session.angles) {
          setIsReceivingMobile(true);
          if (onSaveAngles) {
            onSaveAngles(data.session.angles);
          }
          setTimeout(() => {
            setIsReceivingMobile(false);
            onClose();
          }, 1200);
        }
      } catch (_) {}
    }, 2000);

    return () => clearInterval(interval);
  }, [isOpen, mode, sessionId, onSaveAngles, onClose]);

  if (!isOpen) return null;

  const captureCurrentFrame = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.88);

    const updated = [...capturedFrames, { step: STEPS[currentStepIndex].id, dataUrl }];
    setCapturedFrames(updated);

    if (currentStepIndex < STEPS.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    }
  };

  const triggerCaptureWithCountdown = () => {
    setCountdown(3);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          captureCurrentFrame();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleFinish = () => {
    if (capturedFrames.length > 0 && onSaveAngles) {
      onSaveAngles(capturedFrames.map((f) => f.dataUrl));
      stopCamera();
      onClose();
    }
  };

  const qrUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/?mobile-scan=true&session=${encodeURIComponent(sessionId)}&avatar=${encodeURIComponent(avatarName)}`
    : `https://studio.mapau.pro/?mobile-scan=true&session=${encodeURIComponent(sessionId)}&avatar=${encodeURIComponent(avatarName)}`;
  const qrImageSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(qrUrl)}&color=ffffff&bgcolor=111420`;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(5, 7, 12, 0.85)',
      backdropFilter: 'blur(16px)',
      zIndex: 1100,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: '#111420',
        borderRadius: '16px',
        border: '1px solid rgba(168, 85, 247, 0.3)',
        boxShadow: '0 25px 60px -15px rgba(0,0,0,0.9), 0 0 40px rgba(168,85,247,0.15)',
        width: '100%',
        maxWidth: '640px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '24px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ background: 'rgba(168,85,247,0.15)', padding: '6px', borderRadius: '8px', color: '#c084fc' }}>
              <Camera size={18} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#f8fafc' }}>
                Escáner Facial 3D Biométrico
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#94a3b8' }}>
                Fijación de identidad multi-ángulo para: <strong>{avatarName}</strong>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { stopCamera(); onClose(); }}
            style={{ background: 'transparent', border: 'none', color: '#64748b', fontSize: '20px', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>

        {/* Mode Selector */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', background: '#0d0f17', padding: '4px', borderRadius: '8px' }}>
          <button
            type="button"
            onClick={() => setMode('webcam')}
            style={{
              padding: '8px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              background: mode === 'webcam' ? 'rgba(168,85,247,0.25)' : 'transparent',
              color: mode === 'webcam' ? '#f8fafc' : '#94a3b8'
            }}
          >
            💻 Cámara Web de esta PC
          </button>
          <button
            type="button"
            onClick={() => setMode('qr')}
            style={{
              padding: '8px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              background: mode === 'qr' ? 'rgba(168,85,247,0.25)' : 'transparent',
              color: mode === 'qr' ? '#f8fafc' : '#94a3b8'
            }}
          >
            📱 Escanear con Celular (QR)
          </button>
        </div>

        {mode === 'webcam' ? (
          <>
            {/* Viewport de Cámara con Óvalo Guía */}
            <div style={{
              position: 'relative',
              width: '100%',
              height: '320px',
              background: '#090a0f',
              borderRadius: '12px',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(255,255,255,0.08)'
            }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
              />

              {/* Guía Facial Ovalada */}
              <div style={{
                position: 'absolute',
                width: '190px',
                height: '240px',
                borderRadius: '50%',
                border: '2px dashed rgba(168, 85, 247, 0.7)',
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)',
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {countdown !== null && (
                  <span style={{ fontSize: '48px', fontWeight: 900, color: '#ff9101', textShadow: '0 0 20px rgba(255,145,1,0.8)' }}>
                    {countdown}
                  </span>
                )}
              </div>

              {/* Indicador de Paso */}
              <div style={{
                position: 'absolute',
                top: '12px',
                background: 'rgba(15, 17, 24, 0.85)',
                backdropFilter: 'blur(8px)',
                padding: '6px 14px',
                borderRadius: '20px',
                border: '1px solid rgba(168, 85, 247, 0.4)',
                color: '#f8fafc',
                fontSize: '12px',
                fontWeight: 700
              }}>
                {STEPS[currentStepIndex].label}
              </div>

              <div style={{
                position: 'absolute',
                bottom: '12px',
                background: 'rgba(0,0,0,0.75)',
                padding: '4px 12px',
                borderRadius: '6px',
                color: '#cbd5e1',
                fontSize: '11px'
              }}>
                {STEPS[currentStepIndex].hint}
              </div>
            </div>

            {/* Galería de capturados */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
              {STEPS.map((s, idx) => {
                const captured = capturedFrames[idx];
                return (
                  <div key={s.id} style={{ textAlign: 'center' }}>
                    <div style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '8px',
                      background: '#161926',
                      border: captured ? '2px solid #10b981' : idx === currentStepIndex ? '2px solid #a855f7' : '1px solid rgba(255,255,255,0.1)',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {captured ? (
                        <img src={captured.dataUrl} alt={s.id} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: '11px', color: '#64748b' }}>{idx + 1}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Botones de acción */}
            <div style={{ display: 'flex', gap: '10px' }}>
              {capturedFrames.length < STEPS.length ? (
                <button
                  type="button"
                  onClick={triggerCaptureWithCountdown}
                  disabled={countdown !== null}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '13px',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Camera size={16} /> Capturar Ángulo ({currentStepIndex + 1}/4)
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleFinish}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '13px',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 15px rgba(16,185,129,0.3)'
                  }}
                >
                  <Check size={16} /> Guardar Embedding Multi-Ángulo
                </button>
              )}
            </div>
          </>
        ) : (
          /* Modo Código QR para Celular */
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            padding: '20px',
            background: '#161926',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.08)'
          }}>
            <div style={{
              background: '#fff',
              padding: '12px',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
            }}>
              <img src={qrImageSrc} alt="Escanear con Celular" style={{ width: '180px', height: '180px', display: 'block' }} />
            </div>

            <div style={{ textAlign: 'center', maxWidth: '380px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#f8fafc', marginBottom: '4px' }}>
                Abre la cámara de tu teléfono móvil
              </div>
              <p style={{ fontSize: '11px', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                Escanea el código para calibrar tu rostro con la cámara de alta resolución de tu smartphone. Las tomas se sincronizarán directamente aquí.
              </p>
            </div>

            {isReceivingMobile && (
              <div style={{
                background: 'rgba(16,185,129,0.2)',
                border: '1px solid #10b981',
                borderRadius: '8px',
                padding: '8px 16px',
                color: '#10b981',
                fontSize: '12px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Check size={16} /> ¡Tomas recibidas desde el celular! Guardando...
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
